import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Link, 
  List, 
  ListOrdered, 
  Quote,
  Undo,
  Redo
} from 'lucide-react';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter your question content...',
  className = '',
  disabled = false 
}) => {
  const editorRef = useRef(null);
  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  // Keep editor content in sync with parent value without breaking cursor position
  useEffect(() => {
    if (!editorRef.current) return;
    const currentContent = editorRef.current.innerHTML;
    if (value !== currentContent) {
      editorRef.current.innerHTML = value || '';
      if (!isFocused) {
        editorRef.current.blur();
      }
    }
  }, [value, isFocused]);

  const executeCommand = (command, value = null) => {
    if (disabled) return;
    
    // Save current state to history
    const currentContent = editorRef.current?.innerHTML || '';
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(currentContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // Execute the command
    document.execCommand(command, false, value);
    
    // Update the content
    const newContent = editorRef.current?.innerHTML || '';
    onChange(newContent);
    
    // Focus back to editor
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      editorRef.current.innerHTML = history[newIndex];
      onChange(history[newIndex]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      editorRef.current.innerHTML = history[newIndex];
      onChange(history[newIndex]);
    }
  };

  const handleInput = () => {
    const content = editorRef.current?.innerHTML || '';
    onChange(content);
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          executeCommand('italic');
          break;
        case 'k':
          e.preventDefault();
          insertLink();
          break;
        case 'z':
          e.preventDefault();
          if (e.shiftKey) {
            redo();
          } else {
            undo();
          }
          break;
        default:
          // No action for other keys
          break;
      }
    }
  };

  const ToolbarButton = ({ icon: Icon, onClick, title, isActive = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded hover:bg-gray-100 transition-colors ${
        isActive ? 'bg-gray-200' : ''
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className={`border border-gray-300 rounded-md ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center border-b border-gray-300 bg-gray-50 p-2 space-x-1">
        <ToolbarButton
          icon={Undo}
          onClick={undo}
          title="Undo (Ctrl+Z)"
          isActive={historyIndex > 0}
        />
        <ToolbarButton
          icon={Redo}
          onClick={redo}
          title="Redo (Ctrl+Shift+Z)"
          isActive={historyIndex < history.length - 1}
        />
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          icon={Bold}
          onClick={() => executeCommand('bold')}
          title="Bold (Ctrl+B)"
        />
        <ToolbarButton
          icon={Italic}
          onClick={() => executeCommand('italic')}
          title="Italic (Ctrl+I)"
        />
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          icon={Link}
          onClick={insertLink}
          title="Insert Link (Ctrl+K)"
        />
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          icon={List}
          onClick={() => executeCommand('insertUnorderedList')}
          title="Bullet List"
        />
        <ToolbarButton
          icon={ListOrdered}
          onClick={() => executeCommand('insertOrderedList')}
          title="Numbered List"
        />
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          icon={Quote}
          onClick={() => executeCommand('formatBlock', 'blockquote')}
          title="Quote"
        />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`min-h-[80px] p-3 focus:outline-none ${
          disabled ? 'bg-gray-50 cursor-not-allowed' : ''
        }`}
        style={{
          lineHeight: '1.5',
          fontSize: '14px'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {/* Placeholder */}
      {!value && (
        <div 
          className="absolute top-16 left-3 text-gray-400 pointer-events-none"
          style={{ fontSize: '14px', lineHeight: '1.5' }}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
